import ThemeInterface from '@/theme-interface'

// Layout constants
export const DEFAULT_MIN_HEIGHT = 98
export const LIST_PADDING = '40px'
export const TOOLBAR_Z_INDEX = 1000

/**
 * Gets inline-specific styles for the editor
 */
export const getInlineStyles = (inFocus: boolean, theme: ThemeInterface) => ({
  '& .MuiTiptap-RichTextContent-root': {
    padding: '4px 12px',
  },
  // Inline mode: minimal styling, primary border on focus
  '& .MuiTiptap-FieldContainer-notchedOutline': {
    border: inFocus ? `1px solid ${theme.primary}` : '1px solid transparent',
  },
  '&:hover .MuiTiptap-FieldContainer-notchedOutline': {
    border: inFocus ? `1px solid ${theme.primary}` : '1px solid transparent',
  },
})

/**
 * Gets normal mode styles for the editor
 */
export const getNormalStyles = (inFocus: boolean, theme: ThemeInterface) => ({
  // Normal mode: standard border behavior
  '&:hover .MuiTiptap-FieldContainer-notchedOutline': {
    borderColor: inFocus ? theme.activeInputBorder : theme.borderColor,
    borderWidth: 1,
    borderRadius: 1,
  },
  '& .MuiTiptap-FieldContainer-notchedOutline': {
    borderColor: inFocus ? theme.activeInputBorder : theme.borderColor,
    borderWidth: 1,
    borderRadius: 1,
  },
})

/**
 * Gets MenuBar styles based on variant
 */
export const getMenuBarStyles = (
  variant: 'sliding' | 'absolute' | 'visible',
  inFocus: boolean,
  theme: ThemeInterface,
) => {
  const baseStyles = {
    backgroundColor: theme.white,
  }

  switch (variant) {
    case 'sliding':
      return {
        ...baseStyles,
        position: 'sticky',
        top: 0,
        zIndex: TOOLBAR_Z_INDEX,
        borderBottom: theme.border,
        '&.MuiTiptap-MenuBar-root': {
          position: 'sticky',
          top: 0,
          zIndex: TOOLBAR_Z_INDEX,
          margin: '1px 1px 0px 1px',
        },
      }
    case 'visible':
      return {
        ...baseStyles,
        position: 'static',
        zIndex: 'auto',
        borderBottom: theme.border,
      }
    default: // absolute
      return {
        ...baseStyles,
        maxWidth: '100%',
        zIndex: TOOLBAR_Z_INDEX,
        opacity: inFocus ? 1 : 0,
        pointerEvents: inFocus ? 'auto' : 'none',
        visibility: inFocus ? 'visible' : 'hidden',
        // Style nested content elements to allow wrapping
        '& .MuiTiptap-MenuBar-content': {
          position: 'absolute',
          display: 'flex',
          flexWrap: 'wrap',
          bottom: '100%',
          backgroundColor: theme.white,
          border: theme.border,
          borderRadius: theme.borderRadius,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        },
        '& .MuiTiptap-MenuControlsContainer-root': {
          flexWrap: 'wrap',
        },
      }
  }
}

/**
 * Gets the main RichTextField styles
 */
export const getRichTextFieldStyles = (
  inFocus: boolean,
  inline: boolean,
  theme: ThemeInterface,
  variant: 'sliding' | 'absolute' | 'visible' = 'absolute',
  background?: string,
) => ({
  background: background ?? (inFocus ? theme.white : inline ? 'transparent' : theme.backgroundColor),
  fontFamily: theme.fontFamily,
  height: '100%',

  '& .MuiColorPicker-root': {
    padding: 0,
  },
  '& .MuiTiptap-RichTextContent-root': {
    fontFamily: theme.fontFamily,
    borderRadius: 1,
    height: '100%',
    overflowY: 'auto',
  },
  '& .MuiTiptap-FieldContainer-root': {
    position: 'relative',
    overflow: variant === 'visible' ? 'auto' : 'visible',
  },
})

/**
 * Gets ProseMirror editor styles
 */
export const getProseMirrorStyles = (
  inline: boolean,
  theme: ThemeInterface,
  minHeight?: string,
) => ({
  minHeight: minHeight || (inline ? 0 : DEFAULT_MIN_HEIGHT),
  paddingTop: 0,
  fontFamily: `${theme.fontFamily} !important`,
  fontSize: 'inherit !important',
  // Conditional padding based on inline mode
  ...(inline
    ? {
      padding: '0 !important',
      margin: '0 !important',
    }
    : {}),
  '& ul, & ol': {
    paddingInlineStart: LIST_PADDING,
  },
  // Override ProseMirror inline styles for all text elements
  '& *': {
    fontFamily: `${theme.fontFamily} !important`,
  },
  // Optimize break element rendering and prevent view jumping
  '& br + br.ProseMirror-trailingBreak': {
    display: 'none', // Hide double breaks
  },
  '& br.hard-break': {
    display: 'block',
    height: '1em', // Ensure proper spacing for Enter-key breaks
  },
  '& br.ProseMirror-trailingBreak': {
    lineHeight: '1em',
    minHeight: '1em',
    // Prevent layout shifts during deletion
    transition: 'none',
  },
  // Prevent cursor jumping during br deletion
  '& .ProseMirror': {
    scrollBehavior: 'auto',
  },
  '& .is-editor-empty:first-of-type::before': {
    color: theme.textPlaceholder,
    content: 'attr(data-placeholder)',
    float: 'left',
    height: 0,
    pointerEvents: 'none',
  },
})

/**
 * Gets sliding variant specific styles
 */
export const getSlidingVariantStyles = (theme: ThemeInterface) => ({
  '& .MuiTiptap-MenuBar-root': {
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    backgroundColor: theme.white,
    borderRadiusTopLeft: theme.borderRadius,
    borderRadiusTopRight: theme.borderRadius,
    borderBottom: theme.border,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
})
